import React, { useState } from "react";
import { Expandable } from "@/components/Expandable";
import { createPortal } from "react-dom";
import { X, Download, UploadCloud, FileText } from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

/** Concept-2 bulk-import dialog — one modal, three tabs:
 *  Collections / Folders / Resources. Each tab renders a matching
 *  CSV template download link + a file input. On submit, the backend
 *  returns `{ created, errors: [{row, error}] }` and we render a summary. */
const KINDS = [
    { key: "collections", label: "Collections" },
    { key: "folders",     label: "Folders" },
    { key: "resources",   label: "Resources" },
];

const KIND_HELP = {
    collections: "Columns: name (required), department_name, description, cover_image.",
    folders:     "Columns: collection_name (required), folder_name (required), parent_folder_name.",
    resources:   "Columns: collection_name, folder_name, name, type (article/video/doc/course/podcast/quiz/book), level (beginner/intermediate/advanced), url, duration_minutes, description.",
};

const BulkImportModal = ({ onClose, onImported }) => {
    const [tab, setTab] = useState("collections");
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const downloadTemplate = () => {
        // The backend serves CSV with Content-Disposition attachment;
        // pointing an <a href> at it triggers the download in one click.
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const token = localStorage.getItem("pas_session_token");
        const suffix = token ? `?_t=${encodeURIComponent(token)}` : "";
        // Since <a download> can't set an Authorization header, we lean on
        // the session-token cookie the app already uses for auth.
        void suffix; // suffix unused when cookie-auth is available
        window.open(`${backend}${API.split(backend).pop() || "/api"}/learning/bulk/template/${tab}`, "_blank");
    };

    const submit = async () => {
        if (!file) { toast.error("Choose a CSV first"); return; }
        setBusy(true);
        setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post(`/learning/bulk/import/${tab}`, fd);
            setResult(r.data);
            const created = r.data?.created || 0;
            const errCount = (r.data?.errors || []).length;
            if (created > 0) {
                toast.success(`Imported ${created} ${tab}${errCount ? ` · ${errCount} row(s) skipped` : ""}`);
                onImported?.();
            } else if (errCount > 0) {
                toast.error(`No rows imported · ${errCount} row(s) had errors`);
            } else {
                toast.info("No rows in the CSV");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Import failed");
        } finally { setBusy(false); }
    };

    return createPortal(
        <div className="lh-c2-modal-backdrop" onClick={onClose} data-testid="lh-c2-bulk-backdrop">
            <div className="lh-c2-newcoll" onClick={(e) => e.stopPropagation()} data-testid="lh-c2-bulk-modal" style={{ maxWidth: 620 }}
                onKeyDown={(e) => {
                    // Enter anywhere = submit the CSV (skipped while typing in a text
                    // area or while the import is already running). Matches the
                    // Enter-to-save UX users asked for in Feb 2026.
                    if (e.key === "Enter" && !busy && file && e.target.tagName !== "TEXTAREA") {
                        e.preventDefault();
                        submit();
                    } else if (e.key === "Escape") {
                        e.preventDefault();
                        onClose?.();
                    }
                }}
            >
                <div className="lh-c2-newcoll-head">
                    <h3>Bulk import</h3>
                    <button type="button" className="lh-c2-modal-close" onClick={onClose} aria-label="Close">
                        <X size={16} />
                    </button>
                </div>
                <div className="lh-c2-newcoll-body">
                    <div className="lh-c2-bulk-tabs" role="tablist">
                        {KINDS.map((k) => (
                            <button
                                key={k.key}
                                role="tab"
                                className={`lh-c2-bulk-tab ${tab === k.key ? "active" : ""}`}
                                onClick={() => { setTab(k.key); setResult(null); setFile(null); }}
                                data-testid={`lh-c2-bulk-tab-${k.key}`}
                            >{k.label}</button>
                        ))}
                    </div>

                    <p className="lh-c2-bulk-help">{KIND_HELP[tab]}</p>

                    <button className="lh-c2-btn" onClick={downloadTemplate} data-testid={`lh-c2-bulk-template-${tab}`}>
                        <Download size={13} /> Download template
                    </button>

                    <label className="lh-c2-bulk-drop" data-testid="lh-c2-bulk-drop">
                        <UploadCloud size={22} />
                        <span className="lh-c2-bulk-drop-title">{file?.name || "Choose a CSV to import"}</span>
                        <span className="lh-c2-bulk-drop-sub">{file ? `${(file.size / 1024).toFixed(1)} KB` : "or drag it here"}</span>
                        <input
                            type="file"
                            accept=".csv,text/csv"
                            hidden
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            data-testid="lh-c2-bulk-file"
                        />
                    </label>

                    {result && (
                        <div className="lh-c2-bulk-result" data-testid="lh-c2-bulk-result">
                            <div className="lh-c2-bulk-result-summary">
                                <FileText size={14} />
                                <span><b>{result.created || 0}</b> imported · <b>{(result.errors || []).length}</b> skipped</span>
                            </div>
                            {(result.errors || []).length > 0 && (
                                <ul className="lh-c2-bulk-errors">
                                    <Expandable
                                        items={result.errors}
                                        limit={20}
                                        className="font-semibold"
                                        testid="bulk-errors-more"
                                        render={(er, i) => <li key={i}>Row {er.row}: {er.error}</li>}
                                    />
                                </ul>
                            )}
                        </div>
                    )}
                </div>
                <div className="lh-c2-newcoll-footer">
                    <button type="button" className="lh-c2-btn" onClick={onClose} data-testid="lh-c2-bulk-cancel">Close</button>
                    <button
                        type="button"
                        className="lh-c2-btn lh-c2-btn-solid"
                        onClick={submit}
                        disabled={busy || !file}
                        data-testid="lh-c2-bulk-submit"
                    >{busy ? "Importing…" : "Import CSV"}</button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default BulkImportModal;
